/**
 * CRC manual override form for one protocol atom.
 */

import { Upload } from "lucide-react";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  closeModal,
  Modal,
  openModal,
  Select,
  TextInput,
} from "risa-oasis-ui_v2";
import { clinicalTrialsService } from "../../../api/clinicalTrialsService";
import type { ScreeningOverride } from "../../../data-model/eligibilityDetail";
import { ModalId } from "../../../enums/modalId";
import { toUserFacingApiErrorMessage } from "../../../utils/apiErrorMessage";
import { ManualOverrideDisposition } from "./manualOverrideDisposition";
import {
  getManualOverrideOptions,
  type ManualOverrideOption,
} from "./manualOverrideOptions";
import { useEligibilityDetailContext } from "../EligibilityDetailContext";

const DIALOG_ID = ModalId.MANUAL_ATOM_OVERRIDE_MODAL;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [".pdf"];

/** Props for the manual override dialog. */
export interface ManualOverrideModalProps {
  isOpen: boolean;
  atomId: string;
  /** Agent screening status for the atom (PASS, FAIL, DATA_MISSING, …). */
  currentAtomStatus: string;
  existingOverride?: ScreeningOverride | null;
  onClose: () => void;
}

/**
 * Modal to set or update a CRC atom override (disposition, comment, optional file).
 *
 * @param props - Open state, atom id, current verdict, existing override, and close handler
 * @returns Manual override dialog
 */
export function ManualOverrideModal({
  isOpen,
  atomId,
  currentAtomStatus,
  existingOverride,
  onClose,
}: ManualOverrideModalProps) {
  const { trialId, mrn, detail, refreshDetail } = useEligibilityDetailContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isModalOpenRef = useRef(false);
  const [disposition, setDisposition] = useState("");
  const [comment, setComment] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const overrideOptions = useMemo(
    () => getManualOverrideOptions(currentAtomStatus),
    [currentAtomStatus],
  );

  const selectOptions = useMemo(
    () =>
      overrideOptions.map((option: ManualOverrideOption) => ({
        value: option.value,
        label: option.label,
      })),
    [overrideOptions],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setDisposition("");
    setComment(existingOverride?.comment ?? "");
    setFile(null);
    setFormKey((current) => current + 1);
  }, [atomId, existingOverride, isOpen, currentAtomStatus]);

  useEffect(() => {
    if (isOpen && !isModalOpenRef.current) {
      openModal(DIALOG_ID);
      isModalOpenRef.current = true;
    } else if (!isOpen && isModalOpenRef.current) {
      closeModal(DIALOG_ID);
      isModalOpenRef.current = false;
    }
  }, [isOpen]);

  useEffect(
    () => () => {
      if (isModalOpenRef.current) {
        closeModal(DIALOG_ID);
        isModalOpenRef.current = false;
      }
    },
    [],
  );

  /**
   * Close the oasis modal and notify the parent.
   */
  const handleClose = () => {
    closeModal(DIALOG_ID);
    isModalOpenRef.current = false;
    onClose();
  };

  /**
   * Validate and store the selected justification file.
   *
   * @param nextFile - Browser-selected upload
   */
  const handleFileChange = (nextFile: File | null) => {
    if (!nextFile) {
      setFile(null);
      return;
    }
    const lowerName = nextFile.name.toLowerCase();
    const allowed = ACCEPTED_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
    if (!allowed) {
      setFile(null);
      toast.error("Supported format: PDF only");
      return;
    }
    if (nextFile.size > MAX_FILE_BYTES) {
      setFile(null);
      toast.error("File must be 5MB or smaller");
      return;
    }
    setFile(nextFile);
  };

  /**
   * Resolve the recommended action for this atom when an intent API call is required.
   *
   * @returns Action id from eligibility detail
   * @throws Error when no actionable row exists for the atom
   */
  const requireActionIdForAtom = (): string => {
    const action = detail.recommended_actions.find(
      (item) => item.atom_id === atomId,
    );
    if (!action?.action_id) {
      throw new Error(
        "No recommended action is available for this criterion. Complete the action from Action Pending or choose a disposition that does not require an action.",
      );
    }
    return action.action_id;
  };

  /**
   * Upload an optional PDF and return its opaque reference.
   *
   * @param actionId - Recommended action the upload must bind to
   * @returns PDF reference or null when no file was selected
   */
  const uploadJustificationIfNeeded = async (
    actionId: string,
  ): Promise<string | null> => {
    if (!file) {
      return null;
    }
    if (
      file.type !== "application/pdf" &&
      !file.name.toLowerCase().endsWith(".pdf")
    ) {
      throw new Error(
        "Only PDF attachments can be saved with an override. Remove the image or upload a PDF.",
      );
    }
    const upload = await clinicalTrialsService.uploadOverrideJustification(
      trialId,
      mrn,
      actionId,
      file,
    );
    return upload.justification_pdf_ref;
  };

  /** Start re-analysis after a committed override without keeping the modal open on failure. */
  const analyzeAfterOverride = async (): Promise<void> => {
    try {
      await clinicalTrialsService.analyzeEligibilityPatient(trialId, mrn);
    } catch (reason) {
      toast.error(
        toUserFacingApiErrorMessage(
          reason,
          "Override saved, but eligibility analysis could not be started",
        ),
      );
    }
  };

  /**
   * Persist the override via the appropriate eligibility API and refresh detail.
   */
  const handleSave = async () => {
    const matchingRecommendedAction = detail.recommended_actions.find(
      (item) => item.atom_id === atomId,
    );
    console.log("[Manual Override] Save clicked", {
      atomId,
      currentAtomStatus,
      disposition,
      matchingRecommendedAction: matchingRecommendedAction ?? null,
      availableRecommendedActions: detail.recommended_actions.map((item) => ({
        actionId: item.action_id,
        atomId: item.atom_id,
        questionId: item.question_id,
        stage: item.stage,
        status: item.status,
      })),
      hasExistingOverride: Boolean(existingOverride),
      hasJustificationDocument: Boolean(file),
    });

    if (!disposition) {
      toast.error("Select an override action");
      return;
    }
    if (!comment.trim()) {
      toast.error("Override comment is required");
      return;
    }
    setIsSaving(true);
    try {
      const trimmedComment = comment.trim();

      switch (disposition as ManualOverrideDisposition) {
        case ManualOverrideDisposition.MarkMetContinue: {
          if (matchingRecommendedAction) {
            const justificationPdfRef = await uploadJustificationIfNeeded(
              matchingRecommendedAction.action_id,
            );
            await clinicalTrialsService.applyEligibilityOverrideIntent(
              trialId,
              mrn,
              matchingRecommendedAction.action_id,
              {
                intent: "MARK_MET_AND_CONTINUE",
                comment: trimmedComment,
                justification_pdf_ref: justificationPdfRef,
              },
            );
          } else {
            if (file) {
              throw new Error(
                "A justification document cannot be attached because this criterion has no recommended action.",
              );
            }
            await clinicalTrialsService.setEligibilityAtomOverride(
              trialId,
              mrn,
              atomId,
              {
                verdict: "PASS",
                comment: trimmedComment,
                justification_pdf_ref: null,
              },
            );
            await analyzeAfterOverride();
          }
          break;
        }
        case ManualOverrideDisposition.MarkNotMetIneligible: {
          const actionId = requireActionIdForAtom();
          const justificationPdfRef =
            await uploadJustificationIfNeeded(actionId);
          await clinicalTrialsService.applyEligibilityOverrideIntent(
            trialId,
            mrn,
            actionId,
            {
              intent: "MARK_INELIGIBLE",
              comment: trimmedComment,
              justification_pdf_ref: justificationPdfRef,
            },
          );
          break;
        }
        case ManualOverrideDisposition.MarkNotMetContinue: {
          let justificationPdfRef: string | null = null;
          if (file) {
            const actionId = requireActionIdForAtom();
            justificationPdfRef = await uploadJustificationIfNeeded(actionId);
          }
          await clinicalTrialsService.setEligibilityAtomOverride(
            trialId,
            mrn,
            atomId,
            {
              verdict: "FAIL",
              comment: trimmedComment,
              justification_pdf_ref: justificationPdfRef,
            },
          );
          break;
        }
        case ManualOverrideDisposition.SkipContinue: {
          let justificationPdfRef: string | null = null;
          if (file) {
            const actionId = requireActionIdForAtom();
            justificationPdfRef = await uploadJustificationIfNeeded(actionId);
          }
          await clinicalTrialsService.setEligibilityAtomOverride(
            trialId,
            mrn,
            atomId,
            {
              verdict: "PASS",
              comment: trimmedComment,
              justification_pdf_ref: justificationPdfRef,
            },
          );
          await analyzeAfterOverride();
          break;
        }
        default:
          toast.error("Unsupported override action");
          return;
      }

      refreshDetail?.();
      handleClose();
    } catch (reason) {
      toast.error(
        toUserFacingApiErrorMessage(reason, "Could not save override"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      dialogId={DIALOG_ID}
      title="Manually Override"
      onSave={() => void handleSave()}
      onCancel={handleClose}
      onClose={handleClose}
      saveButtonText={isSaving ? "Saving…" : "Save"}
      cancelText="Cancel"
      disableSave={isSaving}
    >
      <div key={formKey} className="flex flex-col gap-4">
        <Select
          id="override-disposition"
          label="Override action"
          placeholder="Select override action"
          options={selectOptions}
          defaultValue={disposition}
          value={disposition}
          required
          onOptionChange={(option) => setDisposition(option.value)}
        />

        <div className="flex flex-col gap-1.5">
          <span className="text-x-tiny font-medium text-primaryGray-3">
            Justification Document:
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS.join(",")}
            className="hidden"
            onChange={(event) =>
              handleFileChange(event.target.files?.[0] ?? null)
            }
          />
          <button
            type="button"
            className="flex w-full flex-col items-center gap-2 rounded-lg border border-dashed border-primaryGray-14 px-4 py-6 text-center hover:bg-primaryGray-16"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              handleFileChange(event.dataTransfer.files?.[0] ?? null);
            }}
          >
            <Upload className="h-5 w-5 text-primaryGray-6" aria-hidden />
            <span className="text-sm text-primaryGray-4">
              Drag and drop or{" "}
              <span className="font-semibold text-tertiaryBlue-4">
                click here
              </span>{" "}
              to upload
            </span>
            {file ? (
              <span className="text-x-tiny text-primaryGray-6">
                {file.name}
              </span>
            ) : (
              <span className="text-x-tiny text-primaryGray-6">
                Supported file format: PDF, not exceeding 5MB
              </span>
            )}
          </button>
        </div>

        <TextInput
          id="override-comment"
          label="Override Comment"
          placeholder="e.g. Lab result was re-run on 07/08/2026 and returned within normal range. Attaching updated report."
          defaultValue={comment}
          required
          onChange={(data) => setComment(data.value ?? "")}
        />
      </div>
    </Modal>
  );
}
